import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { useAuthenticatedMutation, useAuthenticatedQuery, useNeedsOnboardingInfo } from '../../../../client/hooks';
import { ParentMyPlayersFields } from './ParentMyPlayersFields';
import {
    ParentMyPlayersFieldsOnRemoveCallbackFragment,
    ParentMyProfileDocument,
    ParentMyProfileQuery,
    ParentMyProfileQueryVariables,
    ParentProfileFormFragment,
    RemoveChildProfileParentProfileFormDocument,
    RemoveChildProfileParentProfileFormMutation,
    RemoveChildProfileParentProfileFormMutationVariables,
    UpdateParentProfileFormDocument,
    UpdateParentProfileFormMutation,
    UpdateParentProfileFormMutationVariables,
} from '../../../../generated/spin-graphql';
import { SpinAppModals, useModals } from '../../../common/SpinModalProvider';
import { AppStateUser, useAppState } from '../../../../client/providers/AppProvider';
import { gql } from '@urql/core';
import { GenericInLayoutPageLoading } from '../../../common/GenericFullPageLoading';
import * as Yup from 'yup';
import { useFormWithSchema } from '../../../../client/hooks/use-form-schema';
import { toast } from 'react-toastify';
import { ParentPersonalInformationFields } from '../../../parents/ParentPersonalInformationFields';

type Props = {
    user: AppStateUser;
};

gql`
    query ParentMyProfile($id: uuid!) {
        espin_parent_profile_by_pk(id: $id) {
            ...ParentProfileForm
        }
    }
`;

export const ParentMyProfile: React.FC<Props> = ({ user }) => {
    const { needs } = useNeedsOnboardingInfo();
    const { isReady, data } = useAuthenticatedQuery<ParentMyProfileQuery, ParentMyProfileQueryVariables>(
        {
            query: ParentMyProfileDocument,
            variables: useMemo(() => ({ id: user.parent_profile?.id }), [user]),
            pause: !!!user.parent_profile,
        },
        ['espin_parent_profile', 'espin_player_profile'],
    );

    if (!isReady) {
        return <GenericInLayoutPageLoading />;
    }

    const { espin_parent_profile_by_pk } = data;

    return (
        <div className="parent-information-container">
            <ParentProfileForm formData={espin_parent_profile_by_pk} />
        </div>
    );
};

gql`
    fragment ParentProfileForm on espin_parent_profile {
        id
        first_name
        last_name
        phone
        child_profiles(where: { user: { deleted_at: { _is_null: true } } }) {
            ...ParentMyPlayersFields
        }
    }

    mutation UpdateParentProfileForm($id: uuid!, $input: espin_parent_profile_set_input!) {
        update_espin_parent_profile_by_pk(pk_columns: { id: $id }, _set: $input) {
            id
        }
    }
    mutation RemoveChildProfileParentProfileForm($playerProfileId: uuid!) {
        removeChildPlayer(playerProfileId: $playerProfileId) {
            valid
        }
    }
`;

const FormDataSchema = Yup.object({
    first_name: Yup.string().required('Required'),
    last_name: Yup.string().required('Required'),
    phone: Yup.string().required('Required'),
});
type FormData = Yup.InferType<typeof FormDataSchema>;
function ParentProfileForm({ formData }: { formData: ParentProfileFormFragment }) {
    const { reloadUser } = useAppState();
    const {
        register,
        handleSubmit,
        control,
        formState: { isSubmitting, isDirty, errors },
    } = useFormWithSchema(FormDataSchema, {
        defaultValues: { ...formData },
    });

    const [_, updateProfile] = useAuthenticatedMutation<
        UpdateParentProfileFormMutation,
        UpdateParentProfileFormMutationVariables
    >(UpdateParentProfileFormDocument);

    const [__, removeChildFromProfile] = useAuthenticatedMutation<
        RemoveChildProfileParentProfileFormMutation,
        RemoveChildProfileParentProfileFormMutationVariables
    >(RemoveChildProfileParentProfileFormDocument);

    const onRemovePlayer = (player: ParentMyPlayersFieldsOnRemoveCallbackFragment) => {
        openModal(SpinAppModals.CONFIRMATION, {
            popupTitle: 'Remove Player',
            askText: `Are you sure you want to remove ${player.first_name} ${player.last_name} from your players? There is no way to undo this.`,
            confirmCTAText: 'Yes',
            cancelCTAText: 'No',
            cancelCTAButtonColour: 'blue',
            confirmCTAButtonColour: 'red',
            onConfirm: async () => {
                const result = await removeChildFromProfile(
                    { playerProfileId: player.id },
                    { additionalTypenames: ['espin_player_profile', 'espin_parent_profile'] },
                );
                closeActiveModal();
                if (result.error) {
                    toast.error(`There was an error removing the player`);
                } else {
                    toast.success('Player removed');
                }

                // we hard reload our data as our players' subscription data relies on this
                await reloadUser();
            },
            onCancel: () => closeActiveModal(),
        });
    };
    const { openModal, closeActiveModal } = useModals();

    const onSubmit = async (data: FormData) => {
        const result = await updateProfile(
            {
                id: formData.id,
                input: {
                    first_name: data.first_name,
                    last_name: data.last_name,
                    phone: data.phone,
                },
            },
            { additionalTypenames: ['espin_parent_profile'] },
        );
        if (result.error) {
            toast.error('There was an error in updating your profile');
            console.error(result.error);
            return;
        }
        toast.success('Profile updated');
    };

    const onCancel = () => {};

    const progressPercentage = 0;
    return (
        <>
            <form
                className="parent-information-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit)(e);
                }}
            >
                <div className="form-header">
                    <h2 className="form-heading">Personal Information</h2>
                </div>
                <ParentPersonalInformationFields register={register} control={control} />

                <div className="buttons-container">
                    <button className="button large-button blue-button" disabled={isSubmitting || !isDirty}>
                        Save Changes
                    </button>
                </div>
            </form>
            <div className="parent-information-form">
                <ParentMyPlayersFields players={formData.child_profiles} onRemove={onRemovePlayer} />
            </div>
        </>
    );
}
